import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../shared/config/src';

const MoodTracker: React.FC = () => {
  const { themeClasses } = useTheme();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  // Mood tracking state
  const [currentMood, setCurrentMood] = useState<number>(5);
  const [moodDescription, setMoodDescription] = useState<string>('');
  const [activities, setActivities] = useState<string[]>([]);
  const [triggers, setTriggers] = useState<string[]>([]);
  const [notes, setNotes] = useState<string>('');

  const moodOptions = [
    { value: 1, label: 'Very Low', emoji: '😔', color: 'text-red-600' },
    { value: 2, label: 'Low', emoji: '😟', color: 'text-orange-600' },
    { value: 3, label: 'Below Average', emoji: '😐', color: 'text-yellow-600' },
    { value: 4, label: 'Slightly Low', emoji: '🙁', color: 'text-yellow-500' },
    { value: 5, label: 'Neutral', emoji: '😐', color: 'text-gray-600' },
    { value: 6, label: 'Slightly Good', emoji: '🙂', color: 'text-green-500' },
    { value: 7, label: 'Good', emoji: '😊', color: 'text-green-600' },
    { value: 8, label: 'Very Good', emoji: '😄', color: 'text-blue-600' },
    { value: 9, label: 'Excellent', emoji: '🤩', color: 'text-purple-600' },
    { value: 10, label: 'Amazing', emoji: '🥳', color: 'text-pink-600' }
  ];

  const activityOptions = [
    'Exercise', 'Work', 'Socializing', 'Reading', 'Music', 'Nature', 
    'Cooking', 'Gaming', 'Meditation', 'Sleep', 'Art', 'Learning'
  ];

  const triggerOptions = [
    'Work stress', 'Relationship issues', 'Health concerns', 'Financial worries',
    'Social anxiety', 'Family problems', 'Weather', 'Sleep issues', 'None'
  ];

  const handleActivityToggle = (activity: string) => {
    setActivities(prev => 
      prev.includes(activity) 
        ? prev.filter(a => a !== activity)
        : [...prev, activity]
    );
  };

  const handleTriggerToggle = (trigger: string) => {
    setTriggers(prev => 
      prev.includes(trigger) 
        ? prev.filter(t => t !== trigger)
        : [...prev, trigger]
    );
  };

  const handleSave = async () => {
    if (!user) {
      console.error('User not logged in');
      return;
    }

    setIsSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const moodLogRef = doc(db, `users/${user.uid}/mood_logs`, today);
      
      const moodData = {
        uid: user.uid,
        createdAt: new Date(),
        mood: currentMood,
        description: moodDescription,
        activities: activities,
        triggers: triggers,
        notes: notes,
        timestamp: new Date().toISOString()
      };

      await setDoc(moodLogRef, moodData, { merge: true });
      alert('Mood entry saved successfully!');
      
      // Reset form
      setCurrentMood(5);
      setMoodDescription('');
      setActivities([]);
      setTriggers([]);
      setNotes('');
    } catch (error) {
      console.error('Error saving mood data:', error);
      alert('Error saving mood entry. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer>
      <div className="max-w-3xl mx-auto">
        <PageSection title="Mood Tracker" emoji="😊">
          <p className="text-gray-600 mb-6">
            Track your daily mood and identify patterns to better understand your emotional well-being.
          </p>
        </PageSection>
        
        <div className="space-y-6">
          {/* Current Mood */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              How are you feeling right now?
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {moodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setCurrentMood(option.value)}
                  className={`p-3 rounded-lg border-2 transition-all bg-white ${
                    currentMood === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-1">{option.emoji}</div>
                  <div className={`text-sm font-medium ${option.color}`}>
                    {option.value}
                  </div>
                  <div className="text-xs text-gray-600">{option.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Mood Description */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Describe your mood
            </h3>
            <textarea
              value={moodDescription}
              onChange={(e) => setMoodDescription(e.target.value)}
              placeholder="What's contributing to how you feel today?"
              className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>

          {/* Activities */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What activities did you do today?
            </h3>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {activityOptions.map((activity) => (
                <button
                  key={activity}
                  onClick={() => handleActivityToggle(activity)}
                  className={`p-2 rounded-lg text-sm transition-all ${
                    activities.includes(activity)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {activity}
                </button>
              ))}
            </div>
          </div>

          {/* Triggers */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What might be affecting your mood?
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {triggerOptions.map((trigger) => (
                <button
                  key={trigger}
                  onClick={() => handleTriggerToggle(trigger)}
                  className={`p-2 rounded-lg text-sm transition-all ${
                    triggers.includes(trigger)
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {trigger}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Additional notes
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any other thoughts or observations about your mood today?"
              className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Mood Entry'}
          </button>
        </div>
      </div>
    </PageContainer>
  );
};

export default MoodTracker;
